/**
 * The single source of truth for choreography *inside* each scene.
 *
 * timeline.ts owns scene-level opacity and travel; this file owns everything
 * that happens within a scene. Both are pure and DOM-free: every value is a
 * function of the same normalised scroll progress `p`, so stopping scroll
 * freezes the composition exactly where it is and reversing scroll reverses it
 * exactly. No timing value is duplicated in TSX, SCSS or the view controller —
 * the controller turns these numbers into CSS custom properties and the
 * stylesheet only consumes them.
 *
 * Each scene declares a `run`: the window in global `p` over which its internal
 * sequence plays. Cue windows are then expressed in that run's local [0, 1], so
 * retiming a scene means moving one pair of numbers.
 */

import {
  PHASES,
  clamp,
  easeInOutCubic,
  easeOutCubic,
  lerp,
  segment,
  type SceneId,
} from './timeline';

import type { CSSProperties } from 'react';

export type EaseName = 'linear' | 'outCubic' | 'inOutCubic' | 'outQuint';

const EASES: Record<EaseName, (t: number) => number> = {
  linear: (t) => t,
  outCubic: easeOutCubic,
  inOutCubic: easeInOutCubic,
  outQuint: (t) => 1 - Math.pow(1 - t, 5),
};

export type CounterFormat = 'percent' | 'thousands';

export interface Cue {
  /** Property suffix. The engine writes `--r-<scene>-<id>`. */
  id: string;
  /** Window in the scene run's local [0, 1]. For a group, the whole envelope. */
  from: number;
  to: number;
  ease?: EaseName;
  /** Repeat as an indexed group; each item is offset by `stagger`. */
  count?: number;
  /** Local-time offset between consecutive items of a group. */
  stagger?: number;
  /** A visual number counted from scroll progress rather than a reveal. */
  counter?: { from: number; to: number; format: CounterFormat };
  /** False for counter-only cues, which have no stylesheet consumer. */
  write?: boolean;
}

interface SceneInternals {
  /** Window in global `p`. Starts inside the cross-fade so scenes assemble as
   *  they arrive, and ends before the scene's fade-out to leave a reading hold. */
  run: [number, number];
  cues: Cue[];
}

/* --------------------------------------------------------------- the sequences */

export const INTERNAL: Record<SceneId, SceneInternals> = {
  /**
   * The greeting and the composer shell are present from the start (they are
   * the scene's resting state), so the run opens straight into typing.
   */
  /*
   * The Prompt scene's entry, position and exit stay scroll-driven, but its
   * interface demonstration does not: the controls, chips and "More prompts"
   * run on the typing clock (PROMPT_SEQUENCE below). Driving them from scroll
   * meant the sentence could finish and then sit there until the user scrolled
   * several more times, which read as a dead range.
   */
  prompt: {
    run: [0.0, 0.116],
    cues: [],
  },

  /**
   * The interface arrives as one composition, then the card, then the rows. The
   * A -> B hand-off that follows is scene-level and lives in timeline.ts.
   */
  desktop: {
    run: [0.144, 0.237],
    cues: [
      { id: 'base', from: 0.0, to: 0.55 },
      { id: 'card', from: 0.3, to: 0.75 },
      { id: 'rows', from: 0.55, to: 0.98, count: 2, stagger: 0.14 },
    ],
  },

  mobile: {
    run: [0.338, 0.421],
    cues: [
      { id: 'base', from: 0.0, to: 0.55 },
      { id: 'card', from: 0.3, to: 0.75 },
      { id: 'rows', from: 0.55, to: 0.98, count: 2, stagger: 0.14 },
    ],
  },

  /** Corporate signage assembly: status, image, QR, rule, message, ticker. */
  signage: {
    run: [0.493, 0.597],
    cues: [
      { id: 'status', from: 0.0, to: 0.26 },
      { id: 'photo', from: 0.14, to: 0.46 },
      { id: 'qr', from: 0.32, to: 0.56 },
      { id: 'rule', from: 0.42, to: 0.64, ease: 'inOutCubic' },
      { id: 'heading', from: 0.52, to: 0.76 },
      { id: 'body', from: 0.6, to: 0.84 },
      { id: 'ticker', from: 0.78, to: 1.0 },
    ],
  },

  /** Reading order, top to bottom, then the supporting tiles. */
  email: {
    run: [0.641, 0.757],
    cues: [
      { id: 'logo', from: 0.0, to: 0.16 },
      { id: 'card', from: 0.06, to: 0.32 },
      { id: 'photo', from: 0.18, to: 0.42 },
      { id: 'alert', from: 0.3, to: 0.48 },
      { id: 'subject', from: 0.36, to: 0.54 },
      { id: 'body', from: 0.42, to: 0.6 },
      { id: 'button', from: 0.48, to: 0.66 },
      { id: 'section', from: 0.58, to: 0.74 },
      { id: 'tiles', from: 0.64, to: 0.94, count: 4, stagger: 0.055 },
      { id: 'more', from: 0.9, to: 1.0 },
    ],
  },

  /**
   * Data becoming available: the shell, then the gauge drawing while its value
   * counts, then the departments, the two stats, and the analyst's own tools.
   */
  report: {
    run: [0.801, 0.964],
    cues: [
      { id: 'shell', from: 0.0, to: 0.14 },
      { id: 'identity', from: 0.06, to: 0.2 },
      { id: 'heading', from: 0.12, to: 0.28 },
      { id: 'gauge-card', from: 0.2, to: 0.34 },
      { id: 'gauge-draw', from: 0.28, to: 0.54, ease: 'outQuint' },
      { id: 'gauge-value', from: 0.3, to: 0.4 },
      {
        id: 'gauge-count',
        from: 0.3,
        to: 0.56,
        ease: 'outQuint',
        write: false,
        counter: { from: 0, to: 86, format: 'percent' },
      },
      { id: 'gauge-caption', from: 0.48, to: 0.6 },
      { id: 'divider', from: 0.5, to: 0.62, ease: 'inOutCubic' },
      { id: 'dept-label', from: 0.5, to: 0.62 },
      { id: 'bar-rows', from: 0.52, to: 0.74, count: 5, stagger: 0.035 },
      { id: 'bars', from: 0.56, to: 0.82, count: 5, stagger: 0.04 },
      { id: 'open-card', from: 0.6, to: 0.72 },
      { id: 'open-value', from: 0.62, to: 0.72 },
      {
        id: 'open-count',
        from: 0.62,
        to: 0.78,
        ease: 'outQuint',
        write: false,
        counter: { from: 0, to: 94, format: 'percent' },
      },
      { id: 'completed-card', from: 0.66, to: 0.78 },
      { id: 'completed-value', from: 0.68, to: 0.78 },
      {
        id: 'completed-count',
        from: 0.68,
        to: 0.86,
        ease: 'outQuint',
        write: false,
        counter: { from: 0, to: 12986, format: 'thousands' },
      },
      { id: 'mini-bar', from: 0.74, to: 0.88, ease: 'inOutCubic' },
      { id: 'observations', from: 0.78, to: 0.9 },
      { id: 'obs-items', from: 0.8, to: 0.94, count: 2, stagger: 0.05 },
      { id: 'reminder', from: 0.88, to: 0.97 },
      { id: 'composer', from: 0.92, to: 1.0 },
    ],
  },
};

/* ------------------------------------------------------------------- typing */

/**
 * The typed sentence is the one thing here that is NOT scrubbed by scroll.
 *
 * Letter-by-letter scrubbing tied the sentence to scroll velocity, so a fast
 * flick typed it instantly and a slow drag stalled it mid-word. It now runs once
 * on its own clock, started when the white composer first becomes recognisably
 * visible, and it is latched afterwards: it never replays on the way back up.
 * Everything else about the scene stays scroll-driven.
 *
 * The clock is a requestAnimationFrame accumulator kept separate from the
 * scroll render path — no timers, and nothing about it touches or delays
 * native scrolling.
 */
export const TYPING = {
  /** Fraction of the composer that must be on screen to start. */
  visibleRatio: 0.15,
  /**
   * Delay when the threshold is crossed by scrolling. This plus startDelayMs is
   * the time to the first glyph — kept short so the field responds to the
   * movement that revealed it.
   */
  scrollDelayMs: 10,
  /**
   * Delay when the composer is already on screen at load, as it is on mobile.
   * This plus startDelayMs lets the first paint settle so the sentence is seen
   * starting, while never leaving the field noticeably blank.
   */
  initialDelayMs: 330,
  startDelayMs: 90,
  durationMs: 2400,
  /** Depth of the per-character cadence variation, 0 = perfectly even. */
  jitter: 0.34,
} as const;

/**
 * The one-time interface demonstration, in milliseconds from the typing start.
 *
 * The controls begin while the sentence is still being typed; the chips only
 * once it is complete, then "More prompts" after the last chip. Stopping
 * scrolling the moment typing starts still plays all of it.
 */
export const PROMPT_SEQUENCE = {
  controls: { count: 4, atTyped: 0.65, staggerMs: 113, itemMs: 260 },
  chips: { count: 3, afterMs: 150, staggerMs: 110, itemMs: 260 },
  more: { afterMs: 200, itemMs: 260 },
} as const;

/** Milliseconds from the typing start at which each element begins revealing. */
const sequenceStarts = () => {
  const sentenceEnd = TYPING.startDelayMs + TYPING.durationMs;
  const controls = Array.from(
    { length: PROMPT_SEQUENCE.controls.count },
    (_, i) =>
      TYPING.startDelayMs +
      TYPING.durationMs * PROMPT_SEQUENCE.controls.atTyped +
      i * PROMPT_SEQUENCE.controls.staggerMs,
  );
  const chips = Array.from(
    { length: PROMPT_SEQUENCE.chips.count },
    (_, i) => sentenceEnd + PROMPT_SEQUENCE.chips.afterMs + i * PROMPT_SEQUENCE.chips.staggerMs,
  );
  const more = chips[chips.length - 1] + PROMPT_SEQUENCE.more.afterMs;
  return { sentenceEnd, controls, chips, more };
};

export const SEQUENCE = sequenceStarts();

/** When the whole demonstration is finished, so the tween can stop. */
export const SEQUENCE_END = SEQUENCE.more + PROMPT_SEQUENCE.more.itemMs;

/**
 * Cumulative, normalised time for each character.
 *
 * A seeded generator, so the cadence is byte-identical on every device and on
 * every visit — the variation is there to stop the rhythm sounding mechanical,
 * not to be random. Computed once per sentence, never per frame.
 */
export const characterCadence = (length: number): number[] => {
  if (length <= 0) {
    return [];
  }
  const weights: number[] = [];
  let seed = 1013904223;
  let total = 0;
  for (let i = 0; i < length; i += 1) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const w = 1 + (seed / 4294967296 - 0.5) * TYPING.jitter;
    weights.push(w);
    total += w;
  }
  const out: number[] = [];
  let acc = 0;
  for (let i = 0; i < length; i += 1) {
    acc += weights[i] / total;
    out.push(acc);
  }
  return out;
};

export interface TypingState {
  chars: number;
  /** Caret opacity while typing; the finished caret blinks from CSS instead. */
  caret: number;
  /** Reveal values for the four composer controls. */
  controls: number[];
  /** Reveal values for the three suggestion chips. */
  chips: number[];
  /** Reveal value for "More prompts". */
  more: number;
  /** True once the sentence itself is complete, which parks the caret. */
  sentenceDone: boolean;
  /** True once the whole demonstration is finished. */
  done: boolean;
}

/** Typed characters, caret and reveal gate from elapsed milliseconds. */
export const typingState = (elapsed: number, cadence: number[]): TypingState => {
  const t = clamp((elapsed - TYPING.startDelayMs) / TYPING.durationMs);
  // Cumulative times are sorted, so a scan from the end is a couple of steps.
  let chars = cadence.length;
  while (chars > 0 && cadence[chars - 1] > t) {
    chars -= 1;
  }
  return {
    chars,
    // Solid while typing: a caret that flashes during typing reads as noise.
    caret: elapsed > 0 ? 1 : 0,
    controls: SEQUENCE.controls.map((at) =>
      segment(elapsed, at, at + PROMPT_SEQUENCE.controls.itemMs),
    ),
    chips: SEQUENCE.chips.map((at) => segment(elapsed, at, at + PROMPT_SEQUENCE.chips.itemMs)),
    more: segment(elapsed, SEQUENCE.more, SEQUENCE.more + PROMPT_SEQUENCE.more.itemMs),
    sentenceDone: elapsed >= SEQUENCE.sentenceEnd,
    done: elapsed >= SEQUENCE_END,
  };
};

/**
 * Scroll windows for the Prompt demonstration.
 *
 * The sentence runs on `q`, the pre-pin progress, not on the scene's own `p`:
 * the composer is already on screen before the stage pins, so driving it from
 * `p` would leave the white field blank for the whole approach. It finishes as
 * the stage pins, and the controls come in under its tail. The chips and "More
 * prompts" then run on `p` inside the scene's own window.
 *
 * Being a pure function of scroll, the whole sequence is reversible: scrolling
 * back up un-types the sentence and re-hides the reveals, exactly as every
 * other scene behaves.
 */
export const PROMPT_SCROLL = {
  sentence: [0, 0.78],
  controls: { count: 4, from: 0.56, to: 0.99, stagger: 0.085 },
  chips: { count: 3, from: 0.012, to: 0.072, stagger: 0.014 },
  more: [0.078, 0.104],
} as const;

const staggered = (
  driver: number,
  group: { count: number; from: number; to: number; stagger: number },
): number[] => {
  const span = group.to - group.from - group.stagger * (group.count - 1);
  return Array.from({ length: group.count }, (_, i) => {
    const from = group.from + group.stagger * i;
    return segment(driver, from, from + Math.max(span, 0.001));
  });
};

/** Typed characters, caret and reveal gates from scroll position alone. */
export const typingStateAtScroll = (q: number, p: number, cadence: number[]): TypingState => {
  const t = segment(q, PROMPT_SCROLL.sentence[0], PROMPT_SCROLL.sentence[1]);
  // Cumulative positions are sorted, so a scan from the end is a couple of steps.
  let chars = cadence.length;
  while (chars > 0 && cadence[chars - 1] > t) {
    chars -= 1;
  }
  const sentenceDone = cadence.length > 0 && chars >= cadence.length;
  const more = segment(p, PROMPT_SCROLL.more[0], PROMPT_SCROLL.more[1]);
  return {
    chars,
    // Solid while the sentence is being written; the finished caret is handed
    // to the stylesheet's slow blink instead.
    caret: chars > 0 && !sentenceDone ? 1 : 0,
    controls: staggered(q, PROMPT_SCROLL.controls),
    chips: staggered(p, PROMPT_SCROLL.chips),
    more,
    sentenceDone,
    done: sentenceDone && more >= 1,
  };
};

/** The finished state, for landing past the scene or under reduced motion. */
export const typingComplete = (cadence: number[]): TypingState => ({
  chars: cadence.length,
  caret: 0,
  controls: SEQUENCE.controls.map(() => 1),
  chips: SEQUENCE.chips.map(() => 1),
  more: 1,
  sentenceDone: true,
  done: true,
});

/* ---------------------------------------------------------- derived helpers */

/** Local progress of a scene's internal sequence, in [0, 1]. */
export const internalProgress = (id: SceneId, p: number): number =>
  segment(p, INTERNAL[id].run[0], INTERNAL[id].run[1]);

/**
 * A group's items share the envelope [from, to]: the stagger eats into each
 * item's own duration rather than pushing the last item past the envelope.
 */
const cueWindow = (cue: Cue, index: number): [number, number] => {
  const stagger = cue.stagger ?? 0;
  const span = cue.to - cue.from - stagger * ((cue.count ?? 1) - 1);
  const start = cue.from + stagger * index;
  return [start, start + Math.max(span, 0.001)];
};

export interface FlatCue {
  scene: SceneId;
  /** Custom property the engine writes, or '' for counter-only cues. */
  prop: string;
  from: number;
  to: number;
  ease: EaseName;
  counter?: { from: number; to: number; format: CounterFormat };
  /** Key of the `[data-count]` element a counter cue drives. */
  countKey?: string;
}

/** Every cue, groups expanded, flattened once for the render loop. */
export const FLAT_CUES: FlatCue[] = (Object.keys(INTERNAL) as SceneId[]).flatMap((scene) =>
  INTERNAL[scene].cues.flatMap((cue) => {
    const count = cue.count ?? 1;
    return Array.from({ length: count }, (_, index) => {
      const [from, to] = cueWindow(cue, index);
      const suffix = count > 1 ? `-${index}` : '';
      return {
        scene,
        prop: cue.write === false ? '' : `--r-${scene}-${cue.id}${suffix}`,
        from,
        to,
        ease: cue.ease ?? 'outCubic',
        counter: cue.counter,
        countKey: cue.counter ? `${scene}-${cue.id}` : undefined,
      };
    });
  }),
);

export const cueValue = (cue: FlatCue, local: number): number =>
  EASES[cue.ease](segment(local, cue.from, cue.to));

export const formatCount = (value: number, format: CounterFormat): string =>
  format === 'percent' ? `${Math.round(value)}%` : Math.round(value).toLocaleString('en-US');

export const counterText = (cue: FlatCue, local: number): string =>
  formatCount(lerp(cue.counter!.from, cue.counter!.to, cueValue(cue, local)), cue.counter!.format);

/**
 * The `--r` reference for one cue, written inline on the element it drives. The
 * property name is built here rather than mirrored in SCSS, so a cue id exists
 * in exactly two places: this file and the element it animates.
 *
 * The fallback chain is what makes every non-animated context correct for free:
 * `--hcc-r0` is 0 only while the pre-paint flag is set and the stage is pinned,
 * and is absent (so the value is 1, the finished state) without JavaScript,
 * under reduced motion, and in the short-landscape linear layout.
 */
export const cueVar = (scene: SceneId, id: string, index?: number): CSSProperties =>
  ({
    '--r': `var(--r-${scene}-${id}${index === undefined ? '' : `-${index}`}, var(--hcc-r0, 1))`,
  }) as CSSProperties;

/* ----------------------------------------------------------- documentation */

/**
 * Reading hold after each internal sequence finishes, as a fraction of the
 * track. Derived, never used at runtime — it exists so the numbers above can be
 * checked against the intent that every scene ends in a still composition.
 */
export const READING_HOLDS: Record<SceneId, number> = (Object.keys(INTERNAL) as SceneId[]).reduce(
  (acc, id) => {
    const exit = PHASES[id].fadeOut?.[0] ?? 1;
    acc[id] = Math.round((exit - INTERNAL[id].run[1]) * 1e4) / 1e4;
    return acc;
  },
  {} as Record<SceneId, number>,
);
